import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import api from '../../../services/api';
import { ensureAdminLogin } from '../../../utils/LoginHelper';
import { 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  FunnelIcon,
  PlusIcon,
  XMarkIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import VehicleDetail from './VehicleDetail';
import VehicleForm from './VehicleForm';

const VehiclesPage = () => {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch vehicles data
  const { data: vehiclesData, isLoading: isQueryLoading, error: queryError, refetch } = useQuery(
    ['vehicles', sortField, sortDirection, currentPage, itemsPerPage, filters],
    async () => {
      setIsLoading(true);
      try {
        // Ensure admin login before making the request
        const isAuthenticated = await ensureAdminLogin();
        console.log('Admin authentication:', isAuthenticated ? 'confirmed' : 'failed');
        
        if (!isAuthenticated) {
          throw new Error('Authentication failed');
        }
        
        // Get the token after ensuring login
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No authentication token found');
          throw new Error('Authentication required');
        }

        console.log('Fetching vehicles with params:', {
          sort_field: sortField,
          sort_direction: sortDirection,
          page: currentPage,
          per_page: itemsPerPage,
          ...filters
        });

        try {
          // Use the api instance instead of direct fetch
          console.log('Making API request to fetch vehicles');
          const response = await api.get('/api/v1/admin/vehicles', {
            params: {
              sort_field: sortField,
              sort_direction: sortDirection,
              page: currentPage,
              per_page: itemsPerPage,
              type: filters.type,
              status: filters.status,
              search: filters.search
            }
          });
          
          // Log the entire response for debugging
          console.log('===== DETAILED API RESPONSE LOGGING =====');
          console.log('Raw API response:', response);
          console.log('Response status:', response.status);
          console.log('Response headers:', response.headers);
          console.log('Response data:', response.data);
          
          // If we have data, log its structure
          if (response.data) {
            console.log('Response data type:', typeof response.data);
            console.log('Response data keys:', Object.keys(response.data));
            
            if (response.data.success) {
              console.log('Success value:', response.data.success);
            }
            
            if (response.data.message) {
              console.log('Message:', response.data.message);
            }
            
            if (response.data.data) {
              console.log('Data property type:', typeof response.data.data);
              console.log('Data property keys:', Object.keys(response.data.data));
              
              if (response.data.data.data) {
                console.log('Nested data array found!');
                console.log('Vehicles array length:', response.data.data.data.length);
                console.log('First vehicle (if exists):', response.data.data.data[0]);
                
                // Log each vehicle in the array
                response.data.data.data.forEach((vehicle, index) => {
                  console.log(`Vehicle ${index + 1}:`, vehicle);
                  console.log(`Vehicle ${index + 1} features:`, vehicle.features);
                  console.log(`Vehicle ${index + 1} features type:`, typeof vehicle.features);
                });
              }
            }
          }
          console.log('===== END DETAILED LOGGING =====');
          
          // Validate response data structure
          if (!response.data) {
            console.error('API returned empty response');
            throw new Error('No data received from API');
          }
          
          // Check if we have a valid data structure
          if (!response.data.success) {
            console.error('API returned unsuccessful response:', response.data);
            throw new Error(response.data.message || 'Failed to fetch vehicles');
          }
          
          setIsLoading(false);
          return response.data;
        } catch (error) {
          console.error('Error fetching vehicles:', error);
          setIsLoading(false);
          throw error;
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error);
        setIsLoading(false);
        throw error;
      }
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Query error:', error);
        setError(error.message || 'Failed to fetch vehicles');
      }
    }
  );

  // Vehicles state derived from API data
  const [vehicles, setVehicles] = useState([]);

  // We no longer use the main query to update the vehicles state
  // This prevents the vehicles from disappearing when the main query returns empty

  // Debug log the data
  useEffect(() => {
    if (vehiclesData) {
      console.log('Vehicles data from API:', vehiclesData);
      
      // Log the structure of the data to help with debugging
      const structure = {
        hasData: !!vehiclesData,
        dataKeys: vehiclesData ? Object.keys(vehiclesData) : [],
        hasSuccess: vehiclesData?.success !== undefined,
        successValue: vehiclesData?.success,
        hasDataProperty: vehiclesData?.data !== undefined,
        dataPropertyType: vehiclesData?.data ? typeof vehiclesData.data : 'undefined',
        dataPropertyIsArray: Array.isArray(vehiclesData?.data),
        dataPropertyHasDataArray: vehiclesData?.data?.data !== undefined,
        vehiclesCount: vehiclesData?.data?.data ? vehiclesData.data.data.length : 0
      };
      
      console.log('Data structure analysis:', structure);
      
      // More detailed logging of the actual data structure
      if (vehiclesData?.data) {
        console.log('Data property contents:', vehiclesData.data);
      }
    }
  }, [vehiclesData]);

  // Extract pagination metadata
  const pagination = {
    currentPage: vehiclesData?.data?.current_page || 1,
    lastPage: vehiclesData?.data?.last_page || 1,
    total: vehiclesData?.data?.total || 0,
    from: vehiclesData?.data?.from || 0,
    to: vehiclesData?.data?.to || 0
  };

  // Ensure admin login on component mount
  useEffect(() => {
    const checkAuth = async () => {
      // Try to login directly
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          console.log('No token found, attempting to login as admin');
          // Direct login attempt
          const loginResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/auth/login`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: 'admin@example.com',
              password: 'password'
            })
          });
          
          if (!loginResponse.ok) {
            throw new Error(`Login failed: ${loginResponse.status}`);
          }
          
          const loginData = await loginResponse.json();
          console.log('Login response:', loginData);
          
          // Handle different API response structures
          if (loginData.success && loginData.data && loginData.data.token) {
            localStorage.setItem('token', loginData.data.token);
            localStorage.setItem('user', JSON.stringify({
              ...loginData.data.user,
              role: 'admin'
            }));
          } else if (loginData.token) {
            localStorage.setItem('token', loginData.token);
            localStorage.setItem('user', JSON.stringify({
              ...loginData.user,
              role: 'admin'
            }));
          }
        }
        
        console.log('Admin authentication confirmed, refreshing vehicles data');
        refetch();
      } catch (error) {
        console.error('Authentication error:', error);
        setError('Authentication failed. Please login again.');
      }
    };
    
    checkAuth();
  }, [refetch]);

  // Add a direct API call to fetch and display vehicles
  // This is now the ONLY source of truth for the vehicles state
  useEffect(() => {
    const fetchVehiclesDirectly = async () => {
      try {
        console.log('Directly fetching vehicles from API...');
        const response = await api.get('/api/v1/admin/vehicles');
        console.log('Direct API response:', response);
        
        if (response.data && response.data.success) {
          const vehiclesData = response.data.data;
          console.log('Direct vehicles data:', vehiclesData);
          
          if (vehiclesData && vehiclesData.data) {
            console.log('Direct vehicles array:', vehiclesData.data);
            console.log('Number of vehicles:', vehiclesData.data.length);
            
            if (vehiclesData.data.length === 0) {
              console.warn('API returned 0 vehicles, but we know there should be vehicles');
              return; // Don't update state with empty array
            }
            
            // Process each vehicle to ensure features are properly formatted
            const processedVehicles = vehiclesData.data.map(vehicle => {
              // Create a copy to avoid mutating the original
              const processedVehicle = {...vehicle};
              
              // Process features if they exist
              if (processedVehicle.features) {
                // If features is a string, try to parse it as JSON
                if (typeof processedVehicle.features === 'string') {
                  try {
                    processedVehicle.features = JSON.parse(processedVehicle.features);
                  } catch (e) {
                    console.error('Error parsing features for vehicle', processedVehicle.id, ':', e);
                    processedVehicle.features = [];
                  }
                }
                
                // If features is still not an array, make it an empty array
                if (!Array.isArray(processedVehicle.features)) {
                  processedVehicle.features = [];
                }
              } else {
                processedVehicle.features = [];
              }
              
              return processedVehicle;
            });
            
            // Set the vehicles state directly from the direct API call
            setVehicles(processedVehicles);
            console.log('Set vehicles state from direct API call:', processedVehicles.length, 'vehicles');
          }
        }
      } catch (error) {
        console.error('Error in direct vehicles fetch:', error);
      }
    };
    
    // Run this when the component mounts AND when refetch is triggered
    fetchVehiclesDirectly();
    
    // Set up a refresh interval to keep the data fresh
    const refreshInterval = setInterval(() => {
      console.log('Auto-refreshing vehicles data...');
      fetchVehiclesDirectly();
    }, 30000); // Refresh every 30 seconds
    
    // Clean up the interval when the component unmounts
    return () => clearInterval(refreshInterval);
  }, []);
  
  // Add a function to manually refresh the vehicles data
  const refreshVehicles = () => {
    console.log('Manually refreshing vehicles data...');
    // Use the direct API call to refresh the data
    const fetchVehiclesDirectly = async () => {
      try {
        console.log('Directly fetching vehicles from API...');
        const response = await api.get('/api/v1/admin/vehicles');
        
        if (response.data && response.data.success) {
          const vehiclesData = response.data.data;
          
          if (vehiclesData && vehiclesData.data && vehiclesData.data.length > 0) {
            // Process each vehicle to ensure features are properly formatted
            const processedVehicles = vehiclesData.data.map(vehicle => {
              // Create a copy to avoid mutating the original
              const processedVehicle = {...vehicle};
              
              // Process features if they exist
              if (processedVehicle.features) {
                // If features is a string, try to parse it as JSON
                if (typeof processedVehicle.features === 'string') {
                  try {
                    processedVehicle.features = JSON.parse(processedVehicle.features);
                  } catch (e) {
                    processedVehicle.features = [];
                  }
                }
                
                // If features is still not an array, make it an empty array
                if (!Array.isArray(processedVehicle.features)) {
                  processedVehicle.features = [];
                }
              } else {
                processedVehicle.features = [];
              }
              
              return processedVehicle;
            });
            
            // Set the vehicles state directly from the direct API call
            setVehicles(processedVehicles);
            console.log('Manually refreshed vehicles state:', processedVehicles.length, 'vehicles');
          }
        }
      } catch (error) {
        console.error('Error in manual vehicles refresh:', error);
      }
    };
    
    fetchVehiclesDirectly();
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters
  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    refetch();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      type: '',
      status: '',
      search: ''
    });
    setCurrentPage(1);
    refetch();
  };

  // View vehicle details
  const viewVehicleDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDetailOpen(true);
  };

  // Edit vehicle
  const editVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsFormOpen(true);
  };

  // Add new vehicle
  const addNewVehicle = () => {
    setSelectedVehicle(null);
    setIsFormOpen(true);
  };

  // Delete vehicle
  const openDeleteModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDeleteModalOpen(true);
  };

  // Handle vehicle form submission
  const handleVehicleSubmit = async (vehicleData) => {
    setIsSubmitting(true);
    
    try {
      let response;
      
      // Check if we're adding or editing
      if (selectedVehicle) {
        // Update existing vehicle
        response = await api.put(`/api/v1/admin/vehicles/${selectedVehicle.id}`, vehicleData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Add new vehicle
        response = await api.post('/api/v1/admin/vehicles', vehicleData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      if (response.data.success) {
        // Refresh the vehicles list
        refetch();
        
        // Close the modal and reset state
        setIsFormOpen(false);
        
        // Show success message
        toast.success(selectedVehicle ? 'Vehicle updated successfully!' : 'Vehicle added successfully!');
      } else {
        setError(response.data.message || 'Failed to save vehicle. Please try again.');
      }
    } catch (error) {
      console.error('API error when saving vehicle:', error);
      
      if (error.response && error.response.status === 422) {
        // Validation errors
        const validationErrors = error.response.data.errors;
        setError('Please correct the following errors: ' + 
          Object.values(validationErrors).flat().join(', '));
      } else if (error.response && error.response.status === 401) {
        // Authentication error
        setError('Authentication failed. Please log in again.');
      } else {
        setError('Failed to save vehicle. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle vehicle deletion
  const handleDeleteVehicle = async () => {
    try {
      const response = await api.delete(`/api/v1/admin/vehicles/${selectedVehicle.id}`);
      
      if (response.data.success) {
        // Refresh the vehicles list
        refetch();
        
        // Close the modal and reset state
        setIsDeleteModalOpen(false);
        setSelectedVehicle(null);
        
        // Show success message
        toast.success('Vehicle deleted successfully!');
      } else {
        setError(response.data.message || 'Failed to delete vehicle. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      setError('Failed to delete vehicle. Please try again later.');
    }
  };

  // Function to render sort indicator
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 ml-1" /> 
      : <ArrowDownIcon className="h-4 w-4 ml-1" />;
  };

  // Function to render sort button
  const renderSortButton = (field, label) => (
    <button
      onClick={() => handleSort(field)}
      className="group inline-flex items-center text-left"
    >
      {label}
      <span className="ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
        {renderSortIcon(field)}
      </span>
    </button>
  );

  // Render status badge
  const renderStatusBadge = (status) => {
    // Handle undefined or null status
    if (status === undefined || status === null) {
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
          Unknown
        </span>
      );
    }
    
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'available':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'maintenance':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'booked':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      case 'unavailable':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      default:
        break;
    }
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };

  // Render vehicle features
  const renderFeatures = (features) => {
    if (!features || !Array.isArray(features) || features.length === 0) {
      return <span className="text-gray-400 italic">No features listed</span>;
    }
    
    return (
      <div className="flex flex-wrap gap-2">
        {features.map((feature, index) => (
          <span 
            key={index} 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
          >
            {feature}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Vehicles Management</h1>
        <div className="flex space-x-3">
          <a 
            href="/admin/vehicles/utilization" 
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            <ChartBarIcon className="h-5 w-5 mr-2" />
            <span>Utilization Dashboard</span>
          </a>
          <button
            onClick={addNewVehicle}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Vehicle
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <FunnelIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white shadow rounded-lg p-4">
          <form onSubmit={applyFilters} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                  Vehicle Type
                </label>
                <select
                  id="type"
                  name="type"
                  value={filters.type}
                  onChange={handleFilterChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="">All Types</option>
                  <option value="SUV">SUV</option>
                  <option value="Van">Van</option>
                  <option value="Minibus">Minibus</option>
                  <option value="Bus">Bus</option>
                  <option value="Sedan">Sedan</option>
                </select>
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="">All Statuses</option>
                  <option value="available">Available</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="booked">Booked</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div>
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Name, Registration..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Reset
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Apply Filters
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Vehicles Table */}
      <div className="bg-white shadow rounded-lg">
        {isLoading || isQueryLoading ? (
          <div className="animate-pulse p-6">
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        ) : error || queryError ? (
          <div className="p-6 text-center">
            <div className="text-red-500 mb-4">{error || queryError}</div>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Retry
            </button>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No vehicles found matching your criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('name', 'Vehicle')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('registration_number', 'Registration')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('type', 'Type')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('capacity', 'Capacity')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('model_year', 'Year')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('status', 'Status')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vehicles.map((vehicle) => (
                  <tr key={vehicle.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {vehicle.image ? (
                            <img 
                              className="h-10 w-10 rounded-full object-cover" 
                              src={`${process.env.REACT_APP_API_URL}/${vehicle.image}`} 
                              alt={vehicle.name} 
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/40?text=No+Image';
                              }}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-xs text-gray-500">No img</span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{vehicle.name}</div>
                          <div className="text-xs text-gray-500">{vehicle.make} {vehicle.model}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{vehicle.registration_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{vehicle.type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.capacity} persons
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {vehicle.model_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderStatusBadge(vehicle.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => viewVehicleDetails(vehicle)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => editVehicle(vehicle)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit Vehicle"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(vehicle)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Vehicle"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex justify-between items-center mt-6">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{pagination.from || 0}</span> to{' '}
            <span className="font-medium">{pagination.to || 0}</span> of{' '}
            <span className="font-medium">{pagination.total || 0}</span> vehicles
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              className={`px-3 py-1 rounded ${
                currentPage === 1 || isLoading
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pagination.lastPage || isLoading}
              className={`px-3 py-1 rounded ${
                currentPage === pagination.lastPage || isLoading
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Vehicle Detail Modal */}
      {isDetailOpen && selectedVehicle && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  onClick={() => setIsDetailOpen(false)}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Vehicle Details</h3>
                  <dl className="mt-2 text-sm text-gray-500">
                    <div className="mt-3">
                      <dt className="font-medium text-gray-500">Registration</dt>
                      <dd className="mt-1">{selectedVehicle.registration_number}</dd>
                    </div>
                    <div className="mt-3">
                      <dt className="font-medium text-gray-500">Make & Model</dt>
                      <dd className="mt-1">{selectedVehicle.make} {selectedVehicle.model}</dd>
                    </div>
                    <div className="mt-3">
                      <dt className="font-medium text-gray-500">Year</dt>
                      <dd className="mt-1">{selectedVehicle.model_year}</dd>
                    </div>
                    <div className="mt-3">
                      <dt className="font-medium text-gray-500">Type</dt>
                      <dd className="mt-1">{selectedVehicle.type}</dd>
                    </div>
                    <div className="mt-3">
                      <dt className="font-medium text-gray-500">Capacity</dt>
                      <dd className="mt-1">{selectedVehicle.capacity} persons</dd>
                    </div>
                    <div className="mt-3">
                      <dt className="font-medium text-gray-500">Status</dt>
                      <dd className="mt-1">{renderStatusBadge(selectedVehicle.status)}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Additional Information</h3>
                  <dl className="mt-2 text-sm text-gray-500">
                    <div className="mt-3">
                      <dt className="font-medium text-gray-500">Features</dt>
                      <dd className="mt-1">
                        {renderFeatures(selectedVehicle.features)}
                      </dd>
                    </div>
                    <div className="mt-3">
                      <dt className="font-medium text-gray-500">Mileage</dt>
                      <dd className="mt-1">{selectedVehicle.mileage || 'Not specified'} km</dd>
                    </div>
                    <div className="mt-3">
                      <dt className="font-medium text-gray-500">Fuel Type</dt>
                      <dd className="mt-1">{selectedVehicle.fuel_type || 'Not specified'}</dd>
                    </div>
                    <div className="mt-3">
                      <dt className="font-medium text-gray-500">Last Maintenance</dt>
                      <dd className="mt-1">{selectedVehicle.last_maintenance_date || 'Not recorded'}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <VehicleForm 
                vehicle={selectedVehicle} 
                onSubmit={handleVehicleSubmit} 
                onCancel={() => setIsFormOpen(false)} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedVehicle && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Vehicle</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the vehicle "{selectedVehicle.name}"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteVehicle}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclesPage;
